import { manifest } from "@/docs/manifest";
import {
  businessAcquisitionCostAtom,
  equityDollarAtom,
  equityInvestorDollarAtom,
  equitySellerRolloverDollarAtom,
  isConventionalLoanTypeAtom,
  isEquityInvestorsIncludedAtom,
  isRealEstateIncludedAtom,
  isRollinDealExpensesAtom,
  isRollinLoanTransactionFeeAtom,
  isRollinSBAGuarantyFeeAtom,
  isSellerRollingOverEquityAtom,
  loanTransactionFeeAtom,
  realEstateDollarValueAtom,
  sbaGuarantyFeeAtom,
  sbaLoanDollarAtom,
  sbaLoanTotalWithOptionalFeesAtom,
  sellersNoteDollarAtom,
  targetPurchasePriceAtom,
  totalDealExpensesAtom,
  workingCapitalBuyerCashAtom,
  workingCapitalEquityContributorAtom,
  workingCapitalFundingSourceAtom,
  workingCapitalIncludedAtom,
} from "@/financeModels/owasco/general";
import { MathResult } from "@/financeModels/types";
import { atom } from "jotai";

export type Item = [
  string,
  MathResult,
  keyof typeof manifest,
  children?: Item[],
];

export const usesAtom = atom((get) => {
  const targetPurchasePrice = get(targetPurchasePriceAtom);
  const dealExpenses = get(totalDealExpensesAtom);
  const loanTransactionFee = get(loanTransactionFeeAtom);
  const sbaguarantyFee = get(sbaGuarantyFeeAtom);
  const isRealEstateIncluded = get(isRealEstateIncludedAtom);
  const realEstateDollarValue = get(realEstateDollarValueAtom);
  const isRollinDealExpenses = get(isRollinDealExpensesAtom);
  const businessAcquisitionCost = get(businessAcquisitionCostAtom);
  const hasSBAAmount = Number(get(sbaLoanDollarAtom)) > 0;
  const workingCapitalIncluded = get(workingCapitalIncludedAtom);
  const workingCapitalBuyerCash = get(workingCapitalBuyerCashAtom);

  const items: Item[] = [
    ...(isRealEstateIncluded
      ? [
          [
            "Business Acquisition",
            businessAcquisitionCost,
            "@/docs/pages/model/overview/uses-acquisition-cost.mdx",
          ],
          [
            "Real Estate Transaction",
            realEstateDollarValue,
            "@/docs/pages/model/overview/uses-real-estate-transaction.mdx",
          ],
        ]
      : [
          [
            "Business Acquisition",
            targetPurchasePrice,
            "@/docs/pages/model/overview/uses-business-acquisition.mdx",
          ],
        ]),

    ...(isRollinDealExpenses && Number(dealExpenses) > 0
      ? [
          [
            "Deal Expenses",
            dealExpenses,
            "@/docs/pages/model/overview/uses-real-estate-transaction.mdx",
          ] as Item,
        ]
      : []),

    ...(hasSBAAmount
      ? [
          [
            "Loan Transaction Fee",
            loanTransactionFee,
            "@/docs/pages/model/overview/uses-loan-transaction-fee.mdx",
          ],
        ]
      : []),
    ...(hasSBAAmount && sbaguarantyFee > 0
      ? [
          [
            "SBA Guaranty Fee",
            sbaguarantyFee,
            "@/docs/pages/model/overview/uses-sba-guaranty-fee.mdx",
          ],
        ]
      : []),

    ...(workingCapitalIncluded && Number(workingCapitalBuyerCash) > 0
      ? [
          [
            "Working Capital",
            workingCapitalBuyerCash,
            "@/docs/pages/model/sidebar/workingCapitalBuyerCash.mdx",
          ],
        ]
      : []),
  ] as Item[];

  return items;
});

export const usesTotalCostAtom = atom((get) => {
  const items = get(usesAtom);
  return items.reduce((acc, [, value]) => acc + Number(value), 0);
});

export const sourcesAtom = atom((get) => {
  const sbaLoanDebt = get(sbaLoanTotalWithOptionalFeesAtom);
  const sellersNote = get(sellersNoteDollarAtom);
  const isEquityInvestorsIncluded = get(isEquityInvestorsIncludedAtom);
  const equityDollar = get(equityDollarAtom);
  const equityInvestorDollar = get(equityInvestorDollarAtom);
  const isRollinLoanTransactionFee = get(isRollinLoanTransactionFeeAtom);
  const isRollinSBAGuarantyFee = get(isRollinSBAGuarantyFeeAtom);
  const loanTransactionFee = get(loanTransactionFeeAtom);
  const sbaguarantyFee = get(sbaGuarantyFeeAtom);
  const hasSBAAmount = Number(get(sbaLoanDollarAtom)) > 0;
  const isConventionalLoanType = get(isConventionalLoanTypeAtom);
  const isSellerRollingOverEquity = get(isSellerRollingOverEquityAtom);
  const equitySellerRolloverDollar = get(equitySellerRolloverDollarAtom);
  const workingCapitalIncluded = get(workingCapitalIncludedAtom);
  const workingCapitalBuyerCash = Number(get(workingCapitalBuyerCashAtom)) || 0;
  const workingCapitalFundingSource = get(workingCapitalFundingSourceAtom);
  const workingCapitalEquityContributor = get(
    workingCapitalEquityContributorAtom,
  );

  const equityDollarWithCapital =
    workingCapitalIncluded &&
    workingCapitalFundingSource === "equity" &&
    workingCapitalEquityContributor === "searcher"
      ? Number(equityDollar) + workingCapitalBuyerCash
      : equityDollar;

  const equirtInvestorDollarWithCapital =
    workingCapitalIncluded &&
    workingCapitalFundingSource === "equity" &&
    workingCapitalEquityContributor === "investors"
      ? Number(equityInvestorDollar) + workingCapitalBuyerCash
      : equityInvestorDollar;

  const items: Item[] = [
    [
      isConventionalLoanType ? "Senior Debt" : "SBA 7(a) Loan",
      sbaLoanDebt,
      isConventionalLoanType
        ? "@/docs/pages/model/overview/sources-senior-debt.mdx"
        : "@/docs/pages/model/overview/sources-sb7a-loan.mdx",
    ],
    [
      "Seller's Note",
      sellersNote,
      "@/docs/pages/model/overview/sources-sellers-note.mdx",
    ],
  ];

  if (!isEquityInvestorsIncluded) {
    items.push([
      "Equity",
      equityDollarWithCapital,
      "@/docs/pages/model/overview/sources-equity.mdx",
    ]);
  } else {
    items.push([
      "Equity - Searcher",
      equityDollarWithCapital,
      "@/docs/pages/model/overview/sources-equity-searcher.mdx",
    ]);
    items.push([
      "Equity - Investor",
      equirtInvestorDollarWithCapital,
      "@/docs/pages/model/overview/sources-equity-investor.mdx",
    ]);
  }

  if (isSellerRollingOverEquity) {
    items.push([
      "Equity - Seller's Rollover",
      equitySellerRolloverDollar,
      "@/docs/pages/model/overview/sources-equity-seller-rollover.mdx",
    ]);
  }

  if (!isRollinLoanTransactionFee && hasSBAAmount)
    items.push([
      "Loan Transaction Fee",
      loanTransactionFee,
      "@/docs/pages/model/overview/sources-loan-transaction-fee.mdx",
    ]);

  if (!isRollinSBAGuarantyFee && hasSBAAmount && sbaguarantyFee > 0)
    items.push([
      "SBA Guaranty Fee",
      sbaguarantyFee,
      "@/docs/pages/model/overview/sources-sba-guaranty-fee.mdx",
    ]);

  return items;
});

export const sourcesTotalCostAtom = atom((get) => {
  const items = get(sourcesAtom);
  return items.reduce((acc, [, value]) => acc + Number(value), 0);
});
